import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { Subscription, timer, fromEvent } from 'rxjs';
import _ from 'lodash';
import { debounce } from 'rxjs/operators';
import {
  Selection,
  select,
  ScaleLinear,
  Axis,
  scaleLinear,
  axisRight,
  range,
  max,
  scaleBand,
  ScaleBand
} from 'd3';
import {
  BarChartConfig,
  CartesianAxisConfig
} from '../../../../shared/models/charts/chart-config-types';
import {
  BarChartData,
  BarGroupData,
  BarChartLegend,
  BarX,
  BarPart,
  BarData
} from '../../../../shared/models/charts/chart-data-types';

// tslint:disable:restrict-plus-operands -- too many violations using w3c and other stuff

@Component({
  providers: [],
  selector: 'muni-gameday-bar-chart',
  templateUrl: './bar-chart.template.html',
  styleUrls: ['./bar-chart.styles.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BarChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  public set config(config: BarChartConfig) {
    this._cfg = _.merge({}, config);
  }

  @Output()
  events: EventEmitter<{ type: string; payload: any }> = new EventEmitter<{
    type: string;
    payload: any;
  }>();

  protected _cfg: BarChartConfig;
  protected cfg: BarChartConfig;
  public showAllEnabled: boolean;
  protected _data: BarChartData = {
    groups: [],
    legend: []
  };
  protected readonly icons = {
    times: '\uf00d',
    link: '\uf0c1',
    unlink: '\uf127'
  };

  @Input()
  public set data(data: BarChartData) {
    if (!data || !data.groups || !Array.isArray(data.groups)) {
      return;
    }

    this._data = {
      ...data,
      groups: data.groups.map((group: BarGroupData) => ({
        ...group,
        orderPeriodClosed: group.bars.every(
          (bar) => !!bar.isOrderPeriodTerminated
        )
      }))
    };

    setTimeout(() => {
      this.initAll();
      this.updateShowAllStatus();
    });
  }

  public get data(): BarChartData {
    return this._data;
  }

  public get window(): Window {
    return window;
  }

  protected svg: Selection<SVGSVGElement, any, any, any>;
  protected chart: Selection<HTMLElement, BarChartData, any, any>;
  protected legend: any; // IAugmentedJQuery;
  protected _x: ScaleBand<any>; //ScaleOrdinal<any, any>;
  protected _subX: ScaleBand<any>; //ScaleOrdinal<any, any>;
  protected y: ScaleLinear<any, any>;

  protected yAxis: Axis<number | { valueOf(): number }>;
  protected elementCfg = {
    topLabelBottomMargin: 10,
    underlineBottomMargin: 6,
    extraBarHeight: 2,
    extraBarExtendWidth: 2,
    xAxisHeight: 15,
    yAxisWidth: 50,
    svgMargins: {
      top: 20,
      bottom: 0,
      left: 10,
      right: 0
    }
  };

  private resizeSubscription: Subscription;

  constructor(private elementRef: ElementRef) {
    this.resizeSubscription = fromEvent(this.window, 'resize')
      .pipe(debounce((x) => timer(100)))
      .subscribe(this.onResize.bind(this));
  }

  public onLegendClick(legendItem: BarChartLegend): void {
    this.emitEvent('legend-click', legendItem.id);
  }

  public onShowAllClick(): void {
    this.emitEvent('showAll-click', '');
  }

  public onMouseOver($event: any): void {
    $event.target.classList.toggle('hovered');
  }

  public getLegendColorStyle(element: any, legendItem: BarChartLegend): any {
    const elementClassList = element.classList;
    const indicator =
      elementClassList.contains('hovered') &&
      elementClassList.contains('inactive');
    const isNativeColor = legendItem.selected || indicator;

    const backgroundColor = isNativeColor ? legendItem.color : 'transparent';
    const border = isNativeColor ? 'none' : '';

    return {
      'background-color': backgroundColor,
      border: border
    };
  }

  public render(): void {
    this.setNewAxisDomain();
    this.drawAxis();
    this.drawBars();
    this.drawTopLabels();
    this.drawBottomLabels();
    this.drawUnderlines();
    this.drawPartialUnderlines();
    this.drawExtra();
  }

  public ngOnInit(): void {
    this.svg = select(this.elementRef.nativeElement.children[0]).select('svg');
    this.legend = this.elementRef.nativeElement.children[0].children[0];
  }

  public ngAfterViewInit(): void {
    this.initAll();
  }

  protected initAll(): void {
    if (
      !this.svg ||
      !this.data.groups ||
      !this.elementRef.nativeElement.parentElement ||
      this.elementRef.nativeElement.parentElement.clientHeight === 0
    ) {
      return;
    }
    this.initCfg();
    this.initSvg();
    this.initAxis();
    this.bindData();
  }

  protected bindData(): void {
    const barGroups = this.chart.selectAll('.bar-group').data(this.data.groups);
    barGroups.enter().append('g').attr('class', 'bar-group');
    barGroups.exit().remove();

    this.render();
  }

  protected x(d?: BarX): any {
    if (d) {
      return this._x(d);
    } else {
      return this._x;
    }
  }

  protected subX(d: BarPart): ScaleBand<any> {
    return this._subX
      .domain(range(d.barsCount || 1).map((el) => el.toString()))
      .range([this.x(d.x), this.x(d.x) + this.x().bandwidth()])
      .padding(0.1);
  }

  protected height(value: number): number {
    return this.cfg.svg.actualHeight - this.y(value);
  }

  protected drawAxis() {
    if (this.data && this.data.groups.length) {
      this.yAxis.scale(this.y).tickSize(this.cfg.svg.actualWidth);
      this.svg
        .select('.y-axis')
        .transition()
        .duration(this.cfg.duration)
        .selection()
        .call(this.yAxis);
    }
  }

  protected setNewAxisDomain() {
    if (this.data && this.data.groups.length) {
      this._x.domain(this.data.groups.map((d) => d.x));
      this.y.domain([
        0,
        max(
          this.data.groups.map((d) =>
            d.bars.reduce(
              (acc, el) => Math.max(acc, el.parts[el.parts.length - 1].y),
              0
            )
          )
        )
      ]);
    }
  }

  protected setNewAxisBands() {
    this._x.rangeRound([0, this.cfg.svg.actualWidth]).padding(0.1);
    this.y.rangeRound([this.cfg.svg.actualHeight, this.elementCfg.xAxisHeight]);
  }

  protected drawBars() {
    const x = (d) => this.subX(d)(d.barIdx || 0);
    const y = (d) => this.y(d.y);
    const height = (d) => {
      const result = this.y(d.y0) - this.y(d.y);
      return result < 1 ? 1 : result;
    };
    const width = (d) => this.subX(d).bandwidth();
    const color = (d) => d.color || undefined;
    const cls = (d) => 'bar ' + (d.cls ? d.cls : '');

    const stackedBars = this.chart
      .selectAll('.bar-group')
      .selectAll('.stacked-bar')
      .data((d: any) => d.bars);
    stackedBars
      .enter()
      .append('g')
      .attr('class', 'stacked-bar')
      .attr('auto-maturity', (d: BarData) => d.parts[0].id) // special for automation guys TODO need to universalise
      .attr('data-automation-e2e-state', (d: BarData) =>
        d.selected ? 'selected' : 'deselected'
      );

    stackedBars.attr('auto-maturity', (d: BarData) => d.parts[0].id); // special for automation guys TODO need to universalise

    stackedBars.exit().remove();

    const bars = this.chart
      .selectAll('.stacked-bar')
      .selectAll('.bar')
      .data((d: any) => d.parts);

    bars
      .enter()
      .append('rect')
      .attr('class', cls)
      .attr('x', x)
      .attr('width', width)
      .attr('y', this.y(0))
      .attr('height', 0)
      .attr('fill', color)
      .on('click', (d: BarPart, i, out) => this.emitEvent('bar-click', d.id))
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y)
      .attr('x', x)
      .attr('height', height);

    bars
      .attr('class', cls)
      .transition()
      .duration(this.cfg.duration)
      .attr('fill', color)
      .attr('x', x)
      .attr('width', width)
      .attr('y', y)
      .attr('height', height);

    bars.exit().remove();
  }

  protected _drawTopLabels(selection, x, y, text) {
    selection
      .enter()
      .insert('text', 'g.stacked-bar')
      .attr('class', 'top-label')
      .attr('x', x)
      .text(text)
      .attr('y', this.cfg.svg.actualHeight)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y);

    selection
      .attr('class', (data: BarData) =>
        data.isOrderPeriodTerminated
          ? 'top-label top-label-period-closed'
          : 'top-label'
      )
      .text(text)
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr('y', y);

    selection.exit().remove();
  }

  protected drawTopLabels() {
    // first SelectAll - for assigning correct parent element
    // d=>[d] - to reach data elements when we do selectAll on selectAll
    this._drawTopLabels(
      this.chart
        .selectAll('.bar-group')
        .selectAll('.bar-group > .top-label')
        .data((d) => [d]),
      (d) => this.x(d.x) + this.x().bandwidth() / 2,
      (d) =>
        this.y(
          d.bars.reduce(
            (acc, el) => Math.max(acc, el.parts[el.parts.length - 1].y),
            0
          )
        ) - this.elementCfg.topLabelBottomMargin,
      (d) => d.topLabel
    );
  }

  protected drawUnderlines() {
    const y = (d) =>
      this.y(
        d.bars.reduce(
          (acc, el) => Math.max(acc, el.parts[el.parts.length - 1].y),
          0
        )
      ) -
      this.elementCfg.underlineBottomMargin +
      1;
    const x = (d) => this.x(d.x);
    const width = (d) => this.x().bandwidth();
    // first SelectAll - for assigning correct parent element
    // d=>[d] - to reach data elements when we do selectAll on selectAll
    const underlined = this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > .underline')
      .data((d) => [d]);

    underlined
      .enter()
      .append('rect')
      .attr('class', 'underline')
      .attr('width', width)
      .attr('height', 1)
      .attr('x', x)
      .attr('y', this.cfg.svg.actualHeight)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y);

    underlined
      .transition()
      .duration(this.cfg.duration)
      .attr('width', width)
      .attr('x', x)
      .attr('y', y);

    underlined.exit().remove();
  }

  protected drawPartialUnderlines() {
    const getX = (bar) => this.subX(bar.parts[bar.parts.length - 1]);
    const y = (d) => this.y(d.maxY) - this.elementCfg.underlineBottomMargin;
    const x = (d) => getX(d.bar)(d.bar.barIdx);
    const width = (d) => getX(d.bar).bandwidth();

    const underlined = this.chart
      .selectAll('.bar-group')
      .selectAll('.bar-group > .partial-underline')
      .data(selectPartialUnderlines);

    underlined
      .enter()
      .append('rect')
      .attr('class', 'partial-underline')
      .attr('width', width)
      .attr('height', 2)
      .attr('x', x)
      .attr('y', this.cfg.svg.actualHeight)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y);

    underlined
      .transition()
      .duration(this.cfg.duration)
      .attr('width', width)
      .attr('x', x)
      .attr('y', y);

    underlined.exit().remove();
  }

  protected _drawBottomLabels(selection, x, y, text) {
    const duration = this.cfg.duration;
    selection
      .enter()
      .insert('text', 'g.stacked-bar')
      .each(function (part) {
        select(this)
          .selectAll('.sub-label')
          .data(text)
          .enter()
          .insert('tspan')
          .attr('class', 'sub-label')
          .attr('x', () => x(part))
          .attr('dy', (d, index) => (index > 0 ? 1 : 0) + 'em')
          .text((d: string) => d);
      })
      .attr('class', 'bottom-label')
      .attr('x', x)
      .attr('y', y)
      .transition()
      .duration(duration)
      .attr('y', y);

    selection
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y)
      .attr('x', x)
      .each(function (part) {
        const subLabels = select(this).selectAll('.sub-label').data(text);

        subLabels
          .enter()
          .insert('tspan')
          .attr('class', 'sub-label')
          .attr('x', () => x(part))
          .attr('dy', (d, index) => (index > 0 ? 1 : 0) + 'em')
          .text((d: string) => d);

        subLabels
          .attr('x', x(part))
          .attr('dy', (d, index) => (index > 0 ? 1 : 0) + 'em')
          .text((d: string) => d);

        subLabels.exit().remove();
      });

    selection
      .exit()
      .each(function (part, i) {
        select(this).selectAll('.sub-label').remove();
      })
      .remove();
  }

  protected drawBottomLabels() {
    // first SelectAll - for assigning correct parent element
    // d=>[d] - to reach data elements when we do selectAll on selectAll
    this._drawBottomLabels(
      this.chart
        .selectAll('.bar-group')
        .selectAll('.bar-group > .bottom-label')
        .data((d) => [d]),
      (d) => this.x(d.x) + this.x().bandwidth() / 2,
      (d) => this.y(0) + this.elementCfg.xAxisHeight,
      (d) => d.bottomLabel
    );
  }

  protected drawExtra() {
    const x = (d) =>
      this.subX(d)(d.barIdx || 0) - this.elementCfg.extraBarExtendWidth / 2;
    const y = (d) => this.y(d.y) - this.elementCfg.extraBarHeight / 2;
    const width = (d) =>
      this.subX(d).bandwidth() + this.elementCfg.extraBarExtendWidth;

    const extraLayer = this.chart
      .selectAll('.bar-group')
      .selectAll('.extra-layer')
      .data((d: any) => d.bars);
    extraLayer.enter().append('g').attr('class', 'extra-layer');
    extraLayer.exit().remove();

    const extra = this.chart
      .selectAll('.extra-layer')
      .selectAll('.extra-bar')
      .data((d: any) => d.extra);

    extra
      .enter()
      .append('rect')
      .attr('class', 'extra-bar')
      .attr('width', width)
      .attr('height', this.elementCfg.extraBarHeight)
      .attr('x', x)
      .attr('y', this.cfg.svg.actualHeight)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y);

    extra
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr('width', width)
      .attr('y', y)
      .attr('height', this.elementCfg.extraBarHeight);

    extra.exit().remove();
  }

  ngOnDestroy() {
    this.resizeSubscription.unsubscribe();
  }

  protected onResize() {
    this.initAll();
  }

  protected emitEvent(type: string, data: any) {
    this.events.emit({ type, payload: data });
  }

  protected initCfg() {
    this.cfg = _.merge(
      {},
      { axis: { y: {} }, svg: {}, elementCfg: {} },
      this._cfg
    );
    this.cfg.margin = this.cfg.margin || this.elementCfg.svgMargins;

    this.cfg.margin = _.merge(
      {},
      { top: 0, left: 0, right: 0, bottom: 0 },
      this.cfg.margin
    );
    this.elementCfg = _.merge({}, this.elementCfg, this.cfg.elementCfg);

    this.cfg.margin.bottom += this.elementCfg.xAxisHeight;

    if (this.cfg.axis.y.display) {
      this.cfg.margin.right += this.elementCfg.yAxisWidth;
    }

    const parent = this.elementRef.nativeElement.parentElement;
    const width = parent.clientWidth;
    const height = parent.clientHeight - this.legend.clientHeight;
    this.cfg.svg = _.merge({}, { width, height }, this.cfg.svg);

    this.cfg.svg.actualWidth =
      this.cfg.svg.width - this.cfg.margin.left - this.cfg.margin.right;
    this.cfg.svg.actualHeight =
      this.cfg.svg.height - this.cfg.margin.top - this.cfg.margin.bottom;

    this.cfg.duration =
      this.cfg.duration !== undefined ? this.cfg.duration : 300;
    this.cfg.showLegend =
      this.cfg.showLegend !== undefined ? this.cfg.showLegend : true;
  }

  protected initSvg() {
    this.svg
      .attr('width', this.cfg.svg.width)
      .attr('height', this.cfg.svg.height + 5);
    const redrawFlag = this.svg.selectAll('g.container-group').size() < 1;

    if (redrawFlag) {
      this.svg.append('g').attr('class', 'container-group');
    }

    const container = this.svg.select('.container-group');
    container.attr('transform', `translate(${this.cfg.margin.left},5)`);

    if (this.cfg.axis.y.display) {
      if (redrawFlag) {
        container.append('g').attr('class', 'axis-group y-axis');
      }
    } else {
      container.select('.y-axis').remove();
    }
    if (redrawFlag) {
      container.append('g').attr('class', 'chart-group');
    }
    this.chart = this.svg.select('.chart-group');
  }

  protected initAxis() {
    function createFormatter(scale, cfg: CartesianAxisConfig): any {
      let formatter = (d) => d;
      if (!cfg.formatter && !cfg.units) {
        return undefined;
      }
      if (cfg.formatter) {
        formatter = cfg.formatter;
      }
      if (cfg.units) {
        return (d) => (scale.domain()[0] === d ? cfg.units : formatter(d));
      }
      return formatter;
    }

    this._x = scaleBand();
    this._subX = scaleBand();
    this.y = scaleLinear();

    this.setNewAxisBands();

    this.yAxis = axisRight(scaleLinear())
      .scale(this.y)
      // .orient(this.cfg.axis.y.orient || 'right')
      .tickFormat(createFormatter(this.y, this.cfg.axis.y))
      .tickSize(this.cfg.svg.actualWidth)
      .ticks(this.cfg.axis.y.ticks || 5);
  }

  private updateShowAllStatus() {
    this.showAllEnabled = !!this._data.legend.find(
      (elem) => elem.selected === false
    );
  }
}

export function selectPartialUnderlines(d: any) {
  if (
    d.bars.length <= 1 ||
    d.bars.every((bar) => bar.isOrderPeriodTerminated)
  ) {
    return [];
  }

  const maxY = d.bars.reduce(
    (acc, bar) => Math.max(acc, bar.parts[bar.parts.length - 1].y),
    0
  );

  return d.bars
    .filter((bar) => bar.isOrderPeriodTerminated)
    .map((bar) => ({ bar, maxY, expanded: d.expanded }));
}
